import { setHostLocale } from "@/i18n/languages";
import type { useSessionStore } from "@/stores/session";
import type { NavBarItem, UserOrganisation } from "@/tenant/types";

const PREVIEW_KEY = "metaship:dev:previewSession";

/** Local-only chrome preview (`?previewSession=1` on a dev host). Lets us
 *  screenshot the host language picker + Boer NAV-DRILL labels without Auth0.
 *  Remembered for the tab — a home-path redirect strips the query. */
export function shouldApplyPreviewSession(search: string, isDev: boolean): boolean {
  if (!isDev) return false;
  if (new URLSearchParams(search).get("previewSession") === "1") {
    try {
      sessionStorage.setItem(PREVIEW_KEY, "1");
    } catch {
      /* ignore */
    }
    return true;
  }
  try {
    return sessionStorage.getItem(PREVIEW_KEY) === "1";
  } catch {
    return false;
  }
}

export function applyPreviewSession(session: ReturnType<typeof useSessionStore>): void {
  const org: UserOrganisation = {
    id: 1,
    name: "Klipfontein",
    token: "preview",
    accountStatus: "active",
    icon: null,
    logo: null,
  };
  const navItems: NavBarItem[] = [
    {
      name: "Boer",
      icon: "agriculture",
      route: "/boer",
      items: [
        { name: "Get Started", icon: "waving_hand", route: "/boer", section: "start" },
        { name: "Dashboard", icon: "dashboard", route: "/boer?section=dashboard", section: "dashboard" },
        { name: "Farms & Blocks", icon: "agriculture", route: "/boer?section=farms", section: "farms" },
        { name: "Atlas", icon: "map", route: "/boer?section=atlas", section: "atlas" },
        { name: "Harvest", icon: "agriculture", route: "/boer?section=harvest", section: "harvest" },
        { name: "Spray", icon: "water_drop", route: "/boer?section=spray", section: "spray" },
        { name: "Compliance", icon: "verified_user", route: "/boer?section=locker", section: "locker" },
        { name: "Settings", icon: "settings", route: "/boer?section=settings", section: "settings" },
      ],
    },
  ];
  session.status = "ready";
  session.user = {
    id: 1,
    email: "preview@theboer.co.za",
    firstname: "Jan",
    surname: "Boer",
    emailAddress: "preview@theboer.co.za",
  };
  session.currentOrg = org;
  session.accessibleOrgs = [org];
  session.navItems = navItems;
  window.MetaShipGlobalState?.updateState({
    user: session.user,
    currentOrg: org,
    organisations: [org],
    locale: "af",
  });
  setHostLocale("af");
}

/**
 * PAC-1387 — local-only preview of the pre-auth screens Auth0 is otherwise
 * needed to reach (`?previewScreen=verify-email` on a dev host). A tester
 * cannot create an unverified Auth0 account, so this is how those screens get
 * looked at in a browser. Dev hosts only, like `?previewSession=1`. Buttons
 * that would call Auth0 / the API are live — this only sets the starting state.
 */
export const PREVIEW_SCREENS = ["verify-email", "invite-only", "no-access", "error"] as const;
export type PreviewScreen = (typeof PREVIEW_SCREENS)[number];

export function previewScreenFrom(search: string, isDev: boolean): PreviewScreen | null {
  if (!isDev) return null;
  const value = new URLSearchParams(search).get("previewScreen");
  return (PREVIEW_SCREENS as readonly string[]).includes(value ?? "") ? (value as PreviewScreen) : null;
}

const PREVIEW_EMAIL = "new.packer@example.com";
const PREVIEW_RAW_ERROR = "GET /user-access/organisation failed: 500 internal error";

export function applyPreviewScreen(session: ReturnType<typeof useSessionStore>, screen: PreviewScreen): void {
  session.user = {
    id: 0,
    email: PREVIEW_EMAIL,
    firstname: "New",
    surname: "Packer",
    emailAddress: PREVIEW_EMAIL,
  };
  session.hasAnyOrganisation = screen === "no-access";
  if (screen === "verify-email") session.status = "verify-email";
  if (screen === "invite-only" || screen === "no-access") session.status = "no-access";
  if (screen === "error") session.fail(PREVIEW_RAW_ERROR);
}
